/* eslint react/forbid-prop-types: 0 */
import React, {
  useContext, memo, useRef
} from 'react';
import PropTypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import {
  ProductPod,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductLink,
  ProductIdentifier, ProductBadge,
  ProductImage, PodSection, ProductCompare,
  ProductCustomOptions, PodSpacer
} from '@thd-olt-component-react/product-pod';
import { AddToQuote } from '@thd-olt-component-react/add-to-quote';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { Price } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { NoteButton, ProductNote } from '../note';
import { ProductListContext } from '../ProductListContext';
import { DeleteButton } from '../delete-button/DeleteButton';
import { ReplaceButton } from '../replace-button/ReplaceButton';
import { MoreMenu } from '../more-menu/MoreMenu';
import { QuantityStepper } from '../quantity-stepper/QuantityStepper';
import { isItemEqual, isGeneralMerchProduct, getIntentForReplace } from '../productListHelper';
import { ListViewPlaceHolder } from './ListViewPlaceHolder';

export const ListPod = memo(({
  handleQuantityUpdate,
  itemId,
  podIndex,
  quantity: quantityProp,
  showStoreSku,
  storeId,
  searchKeyword
}) => {
  const {
    productsDisplayed,
    isSharedList,
    removeListItems,
    isRemoving,
    list
  } = useContext(ProductListContext);
  const { channel } = useContext(ExperienceContext);
  const { isB2BCustomer = false } = useThdCustomer();
  const isMobile = channel === 'mobile';
  const noteButtonRef = useRef(null);

  const product = productsDisplayed.find((item) => item.itemId === itemId);

  // Find the item in list.groups to check for comments (notes) and get group context
  const listItemWithGroup = list?.groups
    ?.filter((group) => group.items)
    ?.map((group) => {
      const foundItem = group.items.find((item) => item.itemId === itemId);
      return foundItem ? { item: foundItem, groupId: group.groupId } : null;
    })
    ?.find((result) => result) || null;

  const listItem = listItemWithGroup?.item || null;
  const groupId = listItemWithGroup?.groupId || null;
  const hasNote = !!listItem?.comments;
  const intentId = listItem?.intentId || null;
  const itemOrder = listItem?.itemOrder || podIndex;

  // Check if product is hidden - Duplicate should be disabled for hidden products
  const isHiddenProduct = product?.info?.isHidden || false;

  // Commented out to prevent individual product API calls
  // Rely entirely on batch loading from observer system
  // const { loading } = useDataModel('product', {
  //   variables: {
  //     itemId,
  //   },
  //   skip: !itemId || Boolean(product)
  // });

  const noATCFulfillment = isGeneralMerchProduct(product);

  const handleOpenNoteDrawer = () => {
    if (noteButtonRef.current) {
      noteButtonRef.current.click();
    }
  };

  // Show placeholder until product data is loaded via batch observer system
  if (!product) {
    return (
      <ListViewPlaceHolder count={1} />
    );
  }

  return (
    <ProductPod
      itemId={itemId}
      storeId={storeId}
      padding="sui-p-0"
      analyticsData={{
        parent: 'product-list', position: podIndex
      }}
      variables={{ skip: true }}
      render={(pod) => (
        <>
          <div className="sui-w-full sui-flex sui-flex-col">
            <div data-testid={`product-item-${itemId}`} className="sui-w-full sui-flex">
              {/* Section 1: Product Image - col-span-1 */}
              <div className="sui-w-28 sui-h-auto sui-flex-shrink-0 sui-pr-4">
                <PodSection padding="sui-p-0" noPadding columnAlign relative>
                  <ProductImage itemId={pod.itemId} />
                  <SuperSkuMiniSwatches
                    itemId={pod.itemId}
                    storeId={storeId}
                    onChange={pod.onChange}
                    onHover={() => { }}
                    numberOfMiniSwatches={3}
                  />
                </PodSection>
              </div>

              <div className="sui-flex-1 sui-min-w-0 sui-grid sui-grid-cols-10 sui-gap-4">
                {/* Section 2: Product Details (Badge + Header + Identifiers) */}
                <div className="sui-col-span-3">
                  <PodSection padding="sui-p-0" noPadding columnAlign gap="sui-gap-1">
                    <PodSpacer padding={['BOTTOM']}>
                      <ProductBadge itemId={pod.itemId} storeId={storeId} />
                    </PodSpacer>
                    <ProductHeader
                      brand="inline"
                      itemId={pod.itemId}
                      brandTitleMaxLine={2}
                      disableShopThisCollection
                    />
                    <PodSpacer>
                      <ProductIdentifier itemId={pod.itemId} type="itemId" />
                      <ProductIdentifier itemId={pod.itemId} type="model" />
                      {showStoreSku && <ProductIdentifier itemId={pod.itemId} type="storeSku" />}
                    </PodSpacer>
                    {searchKeyword && (
                      <div className="sui-text-primary sui-text-xs sui-pt-1">
                        Searched:
                        {' '}
                        <span>{`"${searchKeyword}"`}</span>
                      </div>
                    )}
                  </PodSection>
                </div>

                {/* Section 3: Inventory (Store + Shipping) */}
                <div className="sui-col-span-3">
                  <PodSection padding="sui-p-0" noPadding columnAlign gap="sui-gap-0">
                    <FulfillmentPodStore itemId={pod.itemId} storeId={pod.storeId} />
                    <FulfillmentPodShipping itemId={pod.itemId} storeId={pod.storeId} />
                  </PodSection>
                </div>

                {/* Section 4: Price + Reviews */}
                <div className="sui-col-span-2">
                  <PodSection padding="sui-p-0" noPadding columnAlign gap="sui-gap-1">
                    <Price
                      itemId={pod.itemId}
                      large={false}
                      storeId={storeId}
                      displayEachUom={false}
                      showPreferredPricingBadge
                      stackWasPrice
                    />
                    <ProductRating itemId={pod.itemId} onlyReviewCount />
                  </PodSection>
                </div>

                {/* Section 5: Quantity + Cart */}
                <div className="sui-col-span-2 sui-flex">
                  <PodSection padding="sui-p-0" noPadding columnAlign gap="sui-gap-1">
                    <QuantityStepper
                      itemId={itemId}
                      quantity={quantityProp}
                      onQuantityChange={handleQuantityUpdate}
                      price={product?.pricing?.value}
                      isReadOnly={isSharedList}
                    />
                    <ProductATC
                      itemId={pod.itemId}
                      checkGeneric
                      quantity={quantityProp}
                      noATCFulfillment={noATCFulfillment}
                    />
                    {isB2BCustomer && (
                      <AddToQuote
                        itemId={pod.itemId}
                        quantity={quantityProp}
                        buttonText="Quote"
                      />
                    )}
                  </PodSection>
                </div>
              </div>

              {/* Section 6: Actions (Delete, More Menu) */}
              {!isSharedList && (
                <div className="sui-flex sui-flex-col sui-justify-start sui-pl-4 sui-flex-shrink-0">
                  {/* Trash and More icons in a row */}
                  <div className="sui-flex sui-flex-row sui-gap-1 sui-justify-end sui-pb-4">
                    <DeleteButton iconSize="small" itemId={itemId} />
                    <MoreMenu
                      iconSize="small"
                      onAddNote={handleOpenNoteDrawer}
                      hasNote={hasNote}
                      itemId={itemId}
                      intent={searchKeyword}
                      intentId={intentId}
                      groupId={groupId}
                      quantity={quantityProp}
                      itemOrder={itemOrder}
                      isHiddenProduct={isHiddenProduct}
                    />
                  </div>
                  {/* Replace button - only show for non-hidden products on desktop */}
                  {!isHiddenProduct && !isMobile && (
                    <ReplaceButton
                      itemId={itemId}
                      intentId={intentId}
                      groupId={groupId}
                      intent={getIntentForReplace(listItem, product)}
                      quantity={quantityProp}
                    />
                  )}
                  {/* Hidden NoteButton to trigger drawer */}
                  <div className="sui-hidden">
                    <NoteButton ref={noteButtonRef} itemId={itemId} />
                  </div>
                </div>
              )}
            </div>

            {/* Product Note - Full Width Below */}
            <ProductNote itemId={itemId} />
          </div>
        </>
      )}
    />
  );
}, isItemEqual);

ListPod.displayName = 'ListPod';

ListPod.propTypes = {
  handleQuantityUpdate: PropTypes.func,
  itemId: PropTypes.string.isRequired,
  podIndex: PropTypes.number,
  quantity: PropTypes.number,
  searchKeyword: PropTypes.string,
  showStoreSku: PropTypes.bool,
  storeId: PropTypes.string
};

ListPod.defaultProps = {
  handleQuantityUpdate: () => { },
  podIndex: 1,
  quantity: 1,
  searchKeyword: '',
  showStoreSku: true,
  storeId: ''
};

ListPod.dataModel = extend(
  NoteButton,
  ProductPod,
  ProductATC,
  ProductImage,
  ProductLink,
  ProductServiceAddOns,
  Price,
  SuperSkuMiniSwatches,
  ProductRating,
  ProductCompare,
  ProductSample,
  ProductIdentifier,
  ProductHeader,
  ProductBadge,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  ProductCustomOptions,
  NonBuyableQuoteCenter,
  AddToQuote
);
